import { useQuery } from '@tanstack/react-query';
import { authedPost } from '@/lib/api';

// Hono backend base for chart-of-accounts writes (mirrors useChartOfAccounts.ts).
const BASE = '/api/v1/chart-of-accounts';

/**
 * Lazily fetches (or creates) the base-currency Opening Balance Equity system
 * account for an organization (useSuspenseAccount pattern).
 *
 * The account is a system account, so it is absent from the normal account
 * pickers by design — this hook exists so the journal-entry modal's
 * "reclassify opening balance equity" scenario can prefill its leg with a
 * stable id. Per-currency OBE siblings are plain accounts and need no special
 * handling (they appear in pickers like any equity account).
 *
 * Routed through the Hono backend: get_or_create_opening_balance_equity_account
 * is SECURITY INVOKER and can INSERT into chart_of_accounts, which is
 * backend-only (RESTRICTIVE verify_backend_token() INSERT policy). The
 * org-creation trigger already seeds the account, so this is almost always an
 * early-return.
 */
export function useOpeningBalanceEquityAccount(
  organizationId: string | undefined,
  options: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: ['opening-balance-equity-account', organizationId],
    queryFn: async () => {
      const { accountId } = await authedPost<{ accountId: string }>(
        `${BASE}/orgs/${organizationId}/opening-balance-equity-account`,
        {},
      );
      return accountId;
    },
    enabled: !!organizationId && (options.enabled ?? true),
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 1,
  });
}
